var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
// amplitude-js ships an ESM build whose only export is the default (the `amplitude`
// namespace object), so it must be imported as a default import.
import amplitude from 'amplitude-js';
import { environment } from '../../environments/environment';
import { StoreService } from '../services/store.service';
import { buildSelectionProperties } from './shared/selection-properties';
var API_KEY = environment.AMPLITUDE.API_KEY;
/**
 * Thin wrapper around the Amplitude browser SDK.
 *
 * The API key is only set in the production/staging environment files, so this
 * service is a silent no-op on local dev (and its methods are safe to call
 * anywhere without guarding at the call site).
 *
 * Every method swallows its errors on purpose: tracking is best effort and must
 * never break a login or a save.
 */
var AnalyticsService = /** @class */ (function () {
    function AnalyticsService(store) {
        this.store = store;
        // Whether identifyUser has run with a client: until it has, a term/stage switch has no
        // user to be attributed to, and refreshSelection() stays silent.
        this.identified = false;
        if (!API_KEY) {
            return;
        }
        try {
            this.client = amplitude.getInstance();
            this.client.init(API_KEY, undefined, {
                // the Amplitude project lives in the US data region
                serverZone: 'US',
                // internal admin app: acquisition parameters are noise
                includeGclid: false,
                includeReferrer: false,
                includeUtm: false,
                logLevel: environment.production ? 'DISABLE' : 'WARN'
            });
        }
        catch (e) { /* ignore */ }
    }
    /**
     * Ties every following event to the logged in user and refreshes the user
     * properties Amplitude can segment by.
     */
    AnalyticsService.prototype.identifyUser = function (user) {
        if (!this.client || !user) {
            return;
        }
        var tenant = user.currentTenant;
        try {
            // the mongo id is the stable identifier: user_id is what Amplitude groups
            // sessions by, the rest are just dimensions
            this.client.setUserId(user._id);
            this.client.setUserProperties(__assign(__assign({ tenant: tenant && tenant.name, tenant_id: tenant && tenant._id }, buildSelectionProperties(this.store)), { role: user.currentRole, 
                // feature flags are per tenant, so "utilization" only means something among the
                // tenants that actually have the feature available
                invoices_enabled: !!(tenant && tenant.invoices_enabled), documents_enabled: !!(tenant && tenant.documents_enabled) }));
            this.identified = true;
        }
        catch (e) { /* ignore */ }
    };
    /**
     * Re-reports the stage/term user properties after the admin switches either picker.
     *
     * Term and stage are session state, not user attributes: without this call every event
     * after a switch would still be attributed to the term/stage the user logged in with.
     * Only the selection is sent — setUserProperties merges, so the tenant, role and feature
     * flags set by `identifyUser` are left untouched.
     */
    AnalyticsService.prototype.refreshSelection = function () {
        if (!this.client || !this.identified) {
            return;
        }
        try {
            this.client.setUserProperties(buildSelectionProperties(this.store));
        }
        catch (e) { /* ignore */ }
    };
    /**
     * Drops the local "a user is identified" flag on logout, so refreshSelection() cannot
     * report the next selection under the previous user's identity.
     */
    AnalyticsService.prototype.clearIdentity = function () {
        this.identified = false;
    };
    AnalyticsService.prototype.track = function (event) {
        if (!this.client) {
            return;
        }
        try {
            this.client.logEvent(event.name, __assign(__assign({}, event.properties), { 
                // staging and production share one Amplitude project
                environment: environment.AMPLITUDE.ENVIRONMENT }));
        }
        catch (e) { /* ignore */ }
    };
    return AnalyticsService;
}());
export { AnalyticsService };
